import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { FormGroup, Label, Input, Button } from 'reactstrap';
import { Drawer } from '../ui/Drawer';
import { useInviteTenantAdmin } from '../../api/platformTenants';
import { PasswordGenerator } from './PasswordGenerator';

interface Props {
  tenantId: number;
  isOpen: boolean;
  onClose: () => void;
}

export function TenantAdminDrawer({ tenantId, isOpen, onClose }: Props) {
  const { user } = useAuth();
  const token = user?.access_token ?? null;

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [passwordTemp, setPasswordTemp] = useState('');
  const [conflict, setConflict] = useState<string | null>(null);
  const [otherError, setOtherError] = useState<string | null>(null);
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const invite = useInviteTenantAdmin(tenantId, token);

  const submit = async (acceptExisting: boolean) => {
    setConflict(null);
    setOtherError(null);
    try {
      await invite.mutateAsync({
        body: { email, display_name: displayName, password_temp: passwordTemp, accept_existing: acceptExisting },
        idempotencyKey,
      });
      onClose();
    } catch (e: any) {
      const status = e?.status;
      const body = e?.body;
      if (status === 409 && body?.code === 'email_exists' && body?.existing_kc_sub) {
        setConflict(body.existing_kc_sub);
      } else {
        setOtherError(body?.detail ?? e?.message ?? 'Errore sconosciuto');
      }
    }
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Aggiungi tenant-admin">
      <form onSubmit={(e) => { e.preventDefault(); submit(false); }} className="d-flex flex-column gap-2">
        <FormGroup>
          <Label for="tad-email" className="mb-1">Email</Label>
          <Input id="tad-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </FormGroup>
        <FormGroup>
          <Label for="tad-display" className="mb-1">Display name</Label>
          <Input id="tad-display" value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={128} required />
        </FormGroup>
        <FormGroup>
          <Label for="tad-password" className="mb-1">Password temporanea</Label>
          <div className="d-flex gap-2">
            <Input
              id="tad-password"
              type="text"
              value={passwordTemp}
              onChange={(e) => setPasswordTemp(e.target.value)}
              minLength={12}
              required
              style={{ flex: 1 }}
            />
            <PasswordGenerator onGenerated={setPasswordTemp} value={passwordTemp} />
          </div>
        </FormGroup>

        {conflict && (
          <div role="alert" className="alert alert-warning py-2 px-3 mb-0">
            <p>
              L'utente <code>{email}</code> esiste già nel sistema (id: {conflict}).
              Vuoi dargli accesso a questa azienda senza re-invitarlo?
            </p>
            <Button type="button" color="primary" size="sm" onClick={() => submit(true)}>
              Dagli accesso a questa azienda
            </Button>
          </div>
        )}

        {otherError && (
          <p className="text-danger small mb-0">{otherError}</p>
        )}

        <div className="d-flex justify-content-end gap-2">
          <Button type="button" color="light" size="sm" onClick={onClose}>Annulla</Button>
          <Button type="submit" color="primary" size="sm" disabled={invite.isPending}>
            Invita tenant-admin
          </Button>
        </div>
      </form>
    </Drawer>
  );
}
